import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

// Initialize dayjs plugins
dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Formats a date for display with proper timezone handling.
 * - For dates with time components: Converts from UTC to local time
 * - For dates without time components: Preserves the date part regardless of timezone
 * 
 * @param date The date to format (string, Date, or Dayjs object)
 * @param format The format string (defaults to 'MMM DD, YYYY h:mm a' for dates with time, 'MMM DD, YYYY' for dates without time)
 * @returns Formatted date string
 */
export const formatDateForDisplay = (
  date: string | Date | dayjs.Dayjs | null | undefined,
  format?: string
): string => {
  if (!date) return '-';
  
  // Convert to dayjs object if it's not already
  const dayjsDate = dayjs.isDayjs(date) ? date : dayjs(date);
  
  // Check if the date has a time component other than 00:00:00
  const hasTimeComponent = 
    dayjsDate.hour() !== 0 || 
    dayjsDate.minute() !== 0 || 
    dayjsDate.second() !== 0;
  
  if (hasTimeComponent) {
    // For dates with time: Convert from UTC to local time
    return dayjs.utc(date).local().format(format || 'MMM DD, YYYY h:mm a');
  } else {
    // For dates without time: Preserve the date part regardless of timezone
    return dayjs.utc(date).format(format || 'MMM DD, YYYY');
  }
};

/**
 * Formats a date for API requests, ensuring it's in UTC.
 * 
 * @param date The date string in YYYY-MM-DD format
 * @returns ISO string in UTC
 */
export const formatDateForApi = (date: string): string => {
  // When sending dates to the API, ensure they're in UTC
  return dayjs.utc(`${date}T00:00:00`).toISOString();
};

/**
 * Formats a date for date picker inputs (YYYY-MM-DD format)
 * 
 * @param isoDate ISO date string
 * @returns Date string in YYYY-MM-DD format
 */
export const formatDateForDatePicker = (isoDate: string | undefined): string => {
  if (!isoDate) return '';
  // For date inputs, we just want the date part without timezone conversion
  return dayjs.utc(isoDate).format('YYYY-MM-DD');
};
